import { Button, Field, Input, InputGroup } from '@chakra-ui/react'
import { useState, type ReactNode } from 'react'
import { isApiError } from '~/api/client'
import { useT } from '~/i18n/use-t'

/** The API refuses a common or short password with a 400 validation error; any other failure is not about the password. */
export const isWeakPasswordError = (err: unknown): boolean =>
  isApiError(err) && err.status === 400 && (err.code === 'validation_error' || err.code === 'bad_request')

/** A password box with a Show / Hide button (the password is never kept in a draft). */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  hint,
  error,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  autoComplete: 'current-password' | 'new-password'
  hint?: ReactNode
  error?: string | null
}) {
  const { t } = useT()
  const [shown, setShown] = useState(false)
  return (
    <Field.Root invalid={Boolean(error)}>
      <Field.Label>{label}</Field.Label>
      <InputGroup
        endElement={
          <Button
            type='button'
            variant='ghost'
            minH='12'
            minW='12'
            aria-pressed={shown}
            onClick={() => setShown((s) => !s)}
          >
            {shown ? t('common.hide') : t('common.show')}
          </Button>
        }
        endElementProps={{ pe: 1, w: 'auto' }}
      >
        <Input
          type={shown ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete}
          enterKeyHint='done'
          maxLength={256}
          onChange={(e) => onChange(e.target.value)}
          pe='24'
        />
      </InputGroup>
      {/* The error first, right under the box; the hint after it. */}
      <Field.ErrorText>{error}</Field.ErrorText>
      {hint && <Field.HelperText fontSize='md'>{hint}</Field.HelperText>}
    </Field.Root>
  )
}
